import _ from 'lodash';
import { forwardRef, useMemo } from 'react';

/**
 * @typedef {Object} ImageContainerProps
 * @property {string} source - Image URL to render as a CSS background-image
 * @property {"contain"|"fill"} [resizeMode] - How the image fills its container.
 *   `'contain'` preserves aspect ratio and centers; `'fill'` (default) stretches to fill.
 * @property {React.CSSProperties} [style] - Additional inline styles for the wrapper div
 */

/**
 * Renders an image as a CSS `background-image` on a div.
 * Supports `resizeMode` to control aspect-ratio behavior.
 *
 * @param {ImageContainerProps & React.HTMLAttributes<HTMLDivElement>} props
 * @param {React.Ref<HTMLDivElement>} ref
 */
export const ImageContainer = forwardRef((props, ref) => {
  const newProps = useMemo(() => {
    const source = props.source;
    const resizeMode = props.resizeMode;
    const newProps = _.omit(props, ['source', 'resizeMode']);

    if (_.isNil(newProps.style)) {
      newProps.style = {};
    }

    newProps.style.backgroundImage = `url(${source})`;

    if (resizeMode === 'contain') {
      newProps.style.backgroundSize = 'contain';
      newProps.style.backgroundRepeat = 'no-repeat';
      newProps.style.backgroundPosition = 'center';
    } else {
      // Default: stretch to fill (original behavior)
      newProps.style.backgroundSize = '100% 100%';
    }

    return newProps;
  }, [props]);

  return <div ref={ref} {...newProps} />;
});
